// =============================================================================
// MeuDriver — Q2: Histórico Detalhado do Estudante
// =============================================================================

import { useState } from 'react'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import {
  BookOpen,
  Clock,
  DollarSign,
  Users,
  Star,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react'
import { CalendarExportButton } from '@/components/shared/CalendarExportButton'
import { useExportStudentBookings } from '@/hooks/useCalendarExport'
import { format, parseISO } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import {
  useStudentStats,
  useStudentLessonHistory,
  useStudentMonthlyLessons,
  STUDENT_HISTORY_PAGE_SIZE,
} from '@/hooks/useStudentHistory'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { formatCurrency, getInitials } from '@/lib/utils'

// ─── KPI Card ────────────────────────────────────────────────────────────────

interface KpiCardProps {
  title: string
  value: string
  sub?: string
  icon: React.ElementType
  color: string
  loading?: boolean
}

function KpiCard({ title, value, sub, icon: Icon, color, loading }: KpiCardProps) {
  return (
    <Card className="dark:bg-gray-900">
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{title}</p>
            {loading ? (
              <Skeleton className="h-7 w-20" />
            ) : (
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{value}</p>
            )}
            {sub && !loading && (
              <p className="text-xs text-muted-foreground">{sub}</p>
            )}
          </div>
          <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${color}`}>
            <Icon className="h-5 w-5 text-white" />
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

// ─── Tooltip customizado ──────────────────────────────────────────────────────

interface ChartTooltipProps {
  active?: boolean
  payload?: Array<{ value: number }>
  label?: string
}

function ChartTooltip({ active, payload, label }: ChartTooltipProps) {
  if (!active || !payload?.length) return null
  return (
    <div className="rounded-xl border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg p-3 text-xs space-y-1">
      <p className="font-semibold text-gray-700 dark:text-gray-300">{label}</p>
      <p className="text-teal-700 font-semibold">{payload[0].value} aula{payload[0].value !== 1 ? 's' : ''}</p>
    </div>
  )
}

// ─── StudentHistory ───────────────────────────────────────────────────────────

export default function StudentHistory() {
  const [page, setPage] = useState(1)

  const { data: stats, isLoading: loadingStats } = useStudentStats()
  const { data: history, isLoading: loadingHistory } = useStudentLessonHistory(page)
  const { data: monthly = [], isLoading: loadingMonthly } = useStudentMonthlyLessons()
  const { handleExport: exportToIcs, isLoading: exportLoading, count: exportCount } = useExportStudentBookings()

  const totalPages = history
    ? Math.ceil(history.total / STUDENT_HISTORY_PAGE_SIZE)
    : 1

  // Formata dados mensais para o gráfico
  const chartData = monthly.map((m) => ({
    mes: format(parseISO(m.month_label + '-01'), 'MMM/yy', { locale: ptBR }),
    Aulas: m.lesson_count,
  }))

  function formatDate(iso: string) {
    try {
      return format(parseISO(iso), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })
    } catch {
      return iso
    }
  }

  function formatBookingType(type: string) {
    const map: Record<string, string> = {
      single:  'Avulsa',
      trial:   'Experimental',
      package: 'Pacote',
    }
    return map[type] ?? type
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Meu histórico</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Acompanhe sua evolução e o resumo de todas as aulas.
          </p>
        </div>
        <button
          onClick={exportToIcs}
          disabled={exportLoading || exportCount === 0}
          className="inline-flex items-center gap-2 rounded-lg border border-border/60 bg-white dark:bg-gray-900 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-slate-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shrink-0"
          data-testid="student-calendar-export-button"
          title={exportCount === 0 ? 'Nenhuma aula para exportar' : 'Exportar para Calendário (.ics)'}
        >
          Exportar para Calendário
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          title="Aulas concluídas"
          value={stats ? String(stats.total_lessons) : '—'}
          sub={`${stats?.lessons_this_month ?? 0} este mês`}
          icon={BookOpen}
          color="bg-teal-500"
          loading={loadingStats}
        />
        <KpiCard
          title="Horas estimadas"
          value={stats ? `${stats.total_hours}h` : '—'}
          sub={`${stats?.lessons_this_year ?? 0} este ano`}
          icon={Clock}
          color="bg-brand-600"
          loading={loadingStats}
        />
        <KpiCard
          title="Total investido"
          value={stats ? formatCurrency(stats.total_spent) : '—'}
          icon={DollarSign}
          color="bg-emerald-500"
          loading={loadingStats}
        />
        <KpiCard
          title="Instrutores"
          value={stats ? String(stats.unique_instructors) : '—'}
          sub={stats?.avg_rating_given ? `Média dada: ${Number(stats.avg_rating_given).toFixed(1)}` : undefined}
          icon={Users}
          color="bg-orange-500"
          loading={loadingStats}
        />
      </div>

      {/* Gráfico de barras: aulas por mês */}
      <Card className="dark:bg-gray-900">
        <CardHeader>
          <CardTitle className="text-base">Aulas por mês (últimos 12 meses)</CardTitle>
        </CardHeader>
        <CardContent>
          {loadingMonthly ? (
            <Skeleton className="h-52 w-full rounded-xl" />
          ) : chartData.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-12">
              Sem dados de aulas ainda.
            </p>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={chartData} margin={{ top: 4, right: 8, bottom: 0, left: -20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="mes" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="Aulas" fill="#14b8a6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Tabela de aulas */}
      <Card className="dark:bg-gray-900">
        <CardHeader>
          <CardTitle className="text-base">
            Aulas concluídas
            {history && (
              <span className="ml-2 text-sm font-normal text-muted-foreground">
                ({history.total} no total)
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loadingHistory ? (
            <div className="space-y-3 p-6">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-14 w-full rounded-lg" />
              ))}
            </div>
          ) : !history || history.rows.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-12">
              Nenhuma aula concluída ainda.
            </p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b dark:border-gray-800">
                      <th className="text-left px-6 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        Instrutor
                      </th>
                      <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground hidden sm:table-cell">
                        Data
                      </th>
                      <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground hidden md:table-cell">
                        Tipo
                      </th>
                      <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        Valor
                      </th>
                      <th className="text-center px-6 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                    {history.rows.map((row) => (
                      <tr
                        key={row.id}
                        className="hover:bg-slate-50 dark:hover:bg-gray-800/50 transition-colors"
                      >
                        <td className="px-6 py-3">
                          <div className="flex items-center gap-2.5">
                            <Avatar className="h-8 w-8 shrink-0">
                              <AvatarImage src={row.instructor_avatar_url ?? undefined} />
                              <AvatarFallback className="text-xs bg-brand-100 text-brand-700">
                                {getInitials(row.instructor_first_name ?? 'IN')}
                              </AvatarFallback>
                            </Avatar>
                            <span className="font-medium text-gray-900 dark:text-gray-100 truncate max-w-[120px]">
                              {row.instructor_first_name?.split(' ')[0] ?? '—'}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground text-xs hidden sm:table-cell">
                          {formatDate(row.scheduled_at)}
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell">
                          <Badge variant="outline" className="text-xs">
                            {formatBookingType(row.booking_type)}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-right font-semibold text-gray-800 dark:text-gray-200">
                          {row.total_price != null ? formatCurrency(row.total_price) : '—'}
                        </td>
                        <td className="px-6 py-3 text-center">
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-teal-700 dark:text-teal-400">
                            <Star className="h-3 w-3 fill-current" />
                            Concluída
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Paginação */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-6 py-4 border-t dark:border-gray-800">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="gap-1.5"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Anterior
                  </Button>
                  <span className="text-xs text-muted-foreground">
                    Página {page} de {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="gap-1.5"
                  >
                    Próxima
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
